/**
 * Task Table Utilities
 *
 * Shared utility functions and constants for task table/kanban styling.
 * Centralizes CSS class mappings and helper functions to avoid duplication.
 */

import { urgencyHexColors } from '../configs/task.config';

// =============================================================================
// CSS CLASS MAPPINGS
// =============================================================================

/**
 * Priority/Urgency to CSS class suffix mapping
 */
export const PRIORITY_CSS_CLASS = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

/**
 * Urgency configuration with colors and labels for tooltips
 */
export const URGENCY_CONFIG = {
  CRITICAL: { color: urgencyHexColors.CRITICAL, label: 'Critical Priority', cssClass: 'critical' },
  HIGH: { color: urgencyHexColors.HIGH, label: 'High Priority', cssClass: 'high' },
  MEDIUM: { color: urgencyHexColors.MEDIUM, label: 'Medium Priority', cssClass: 'medium' },
  LOW: { color: urgencyHexColors.LOW, label: 'Low Priority', cssClass: 'low' },
};

/**
 * Status configuration with colors and CSS class suffixes
 * Used by both Tasks and Projects for visual consistency
 */
export const STATUS_CONFIG = {
  // Task statuses — keys must match API values from statusTypes in task.config.js
  NOT_STARTED: { color: '#94a3b8', label: 'Not Started', cssClass: 'todo' },
  TRIAGED: { color: '#1890ff', label: 'Triaged', cssClass: 'triaged' },
  READY: { color: '#13c2c2', label: 'Ready for Review', cssClass: 'in-review' },
  IN_PROGRESS: { color: '#3b82f6', label: 'In Progress', cssClass: 'in-progress' },
  BLOCKED: { color: '#dc2626', label: 'Blocked', cssClass: 'blocked' },
  ON_HOLD: { color: '#8b5cf6', label: 'On Hold', cssClass: 'on-hold' },
  COMPLETED: { color: '#22c55e', label: 'Completed', cssClass: 'done' },
  MONITORING: { color: '#722ed1', label: 'Monitoring', cssClass: 'monitoring' },
  DUPLICATE: { color: '#8c8c8c', label: 'Duplicate', cssClass: 'cancelled' },
  INPUT_REQUIRED: { color: '#fa8c16', label: 'Input Required', cssClass: 'input-required' },
  NO_LONGER_NEEDED: { color: '#8c8c8c', label: 'No Longer Needed', cssClass: 'cancelled' },
  CANCELED: { color: '#ef4444', label: 'Canceled', cssClass: 'cancelled' },
  // Project-specific statuses
  PLANNING: { color: '#8b5cf6', label: 'Planning', cssClass: 'planning' },
};

/**
 * Avatar colors for assignee display
 */
export const AVATAR_COLORS = [
  '#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#fa8c16',
  '#13c2c2', '#2f54eb', '#a0d911', '#faad14', '#f5222d',
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get avatar background color based on name
 * @param {string} name - User name
 * @returns {string} Hex color code
 */
export const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const index = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

// Terminal statuses that should never show as overdue
const TERMINAL_STATUSES = ['COMPLETED', 'CANCELED', 'CANCELLED', 'DONE', 'NO_LONGER_NEEDED', 'DUPLICATE'];

/**
 * Check if a status is terminal (completed/canceled)
 * @param {string} status - Task status
 * @returns {boolean}
 */
const isTerminalStatus = (status) => {
  if (!status) return false;
  const normalized = status.toUpperCase();
  return TERMINAL_STATUSES.includes(normalized);
};

/**
 * Format due date with overdue detection
 * @param {string|Date} dueDate - Due date
 * @param {string} status - Task status (to exclude completed from overdue)
 * @returns {Object} { text, isOverdue, isToday, diffDays, daysLate }
 */
export const formatDueDate = (dueDate, status) => {
  if (!dueDate) return { text: '—', isOverdue: false, isToday: false, diffDays: null, daysLate: 0 };

  const date = new Date(dueDate);
  const now = new Date();

  // Normalize to start of day for accurate day comparison
  const dateNormalized = new Date(date);
  dateNormalized.setHours(0, 0, 0, 0);
  const nowNormalized = new Date(now);
  nowNormalized.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((dateNormalized - nowNormalized) / 86400000);

  // Don't mark as overdue if task is completed/canceled
  const isOverdue = diffDays < 0 && !isTerminalStatus(status);
  const isToday = diffDays === 0;
  const daysLate = isOverdue ? Math.abs(diffDays) : 0;

  let text;
  if (diffDays === 0) {
    text = 'Today';
  } else if (diffDays === 1) {
    text = 'Tomorrow';
  } else if (diffDays === -1) {
    text = 'Yesterday';
  } else if (diffDays > 0 && diffDays < 7) {
    text = date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    text = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return { text, isOverdue, isToday, diffDays, daysLate };
};

/**
 * Compute row class name based on task properties
 * @param {Object} record - Task record
 * @returns {string} Space-separated class names
 */
export const getRowClassName = (record) => {
  const classes = ['task-table__row'];

  // Priority/Urgency border indicator
  const urgency = record.urgency || 'LOW';
  const priorityClass = PRIORITY_CSS_CLASS[urgency.toUpperCase()];
  if (priorityClass) {
    classes.push(`task-table__row--priority-${priorityClass}`);
  }

  // Overdue row emphasis (formatDueDate already excludes terminal statuses)
  const { isOverdue } = formatDueDate(record.dueDate, record.status);
  if (isOverdue) {
    classes.push('task-table__row--overdue');
  }

  // Completed/Canceled row (dimmed effect)
  if (isTerminalStatus(record.status)) {
    classes.push('task-table__row--completed');
  }

  return classes.join(' ');
};

/**
 * Get relative time string (e.g., "2h", "3d", "1w")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
  return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Get status configuration for a given status key
 * @param {string} status - Status key (e.g., 'IN_PROGRESS')
 * @returns {Object} Status config with color, label, cssClass
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED;
};

/**
 * Format status label from key (e.g., 'IN_PROGRESS' -> 'In Progress')
 * @param {string} status - Status key
 * @returns {string} Formatted label
 */
export const formatStatusLabel = (status) => {
  if (!status) return 'Not Started';
  return status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Compute project row/card class name based on properties
 * @param {Object} record - Project record
 * @returns {string} Space-separated class names
 */
export const getProjectRowClassName = (record) => {
  const classes = ['project-table__row'];

  // Priority border indicator (same as tasks)
  const priority = record.priority || 'LOW';
  const priorityClass = PRIORITY_CSS_CLASS[priority.toUpperCase()];
  if (priorityClass) {
    classes.push(`project-table__row--priority-${priorityClass}`);
  }

  // Overdue row emphasis (based on endDate) - skip for completed/canceled
  if (record.endDate && !isTerminalStatus(record.status)) {
    const endDate = new Date(record.endDate);
    const now = new Date();
    endDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    if (endDate < now) {
      classes.push('project-table__row--overdue');
    }
  }

  // Completed row styling
  if (isTerminalStatus(record.status)) {
    classes.push('project-table__row--completed');
  }

  return classes.join(' ');
};

/**
 * Format project end date with overdue detection
 * @param {string|Date} endDate - Project end date
 * @param {string} status - Project status
 * @returns {Object} { text, isOverdue, daysLeft, daysOverdue }
 */
export const formatProjectDueDate = (endDate, status) => {
  if (!endDate) return { text: null, isOverdue: false, daysLeft: null, daysOverdue: 0 };

  const date = new Date(endDate);
  const now = new Date();

  // Normalize to start of day
  date.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((date - now) / 86400000);

  // Don't mark as overdue if project is completed/canceled
  const isOverdue = diffDays < 0 && !isTerminalStatus(status);
  const daysOverdue = isOverdue ? Math.abs(diffDays) : 0;
  const daysLeft = diffDays >= 0 ? diffDays : 0;

  return {
    text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    isOverdue,
    daysLeft,
    daysOverdue,
    isToday: diffDays === 0,
    isDueSoon: diffDays > 0 && diffDays <= 7,
  };
};
